/**
 * @file    useChartRules.ts
 * @scope   SHARED
 * @summary LEGACY-ONLY rule engine. Holds the Guardrails business rules for the selected chart and
 *          derives the validation flags shown next to the question/calculation pickers.
 * @consumes Guardrails `Rules` payload, the chart option currently selected
 * @produces applicableRules, responseRules, the `required*` flags and `selectRule`
 * @sideEffects None beyond its own local state.
 * @notes Enterprise V2 widgets do not use this engine: their rule list is empty, every flag stays
 *        `false` and the Save guard short-circuits before reaching them.
 */
import { useState } from 'react';
import { SelectOptions } from 'core-ui/MultipleSelectSortable/MultipleSelectSortable';
import { Rules, RuleType } from '../types';

/** Everything the drawer needs from the rule engine. */
export type UseChartRulesResult = {
  /** Rules of the currently selected chart (subset of `responseRules`). */
  applicableRules: Rules[];
  /** Full rule list returned by the last Guardrails response. */
  responseRules: Rules[];
  /** Stores the raw rule list so it can be re-filtered when the chart changes. */
  setResponseRules: React.Dispatch<React.SetStateAction<Rules[]>>;
  /** Re-filters `responseRules` against the rules accepted by the selected chart. */
  filterRules: (rules: Rules[], allowedRuleNames?: string[]) => void;
  /** `true` when the current question/calculation combination breaks every applicable rule. */
  requiredQuestionsCalculations: boolean;
  /** `true` when the questions count is the reason the combination is invalid. */
  requiredQuestions: boolean;
  /** `true` when the calculations count is the reason the combination is invalid. */
  requiredCalculations: boolean;
  /** Evaluates the selection and returns the matching rules (empty when nothing matches). */
  selectRule: (questions?: SelectOptions[], calculations?: SelectOptions[]) => Rules[];
  /** `true` when at least one applicable rule accepts that dimension (controls picker visibility). */
  showQuestionCalculation: (ruleType: RuleType) => boolean;
};

export const useChartRules = (): UseChartRulesResult => {
  const [applicableRules, setApplicableRules] = useState<Rules[]>([]);
  const [responseRules, setResponseRules] = useState<Rules[]>([]);
  const [requiredQuestionsCalculations, setRequiredQuestionsCalculations] =
    useState<boolean>(false);
  const [requiredQuestions, setRequiredQuestions] = useState<boolean>(false);
  const [requiredCalculations, setRequiredCalculations] = useState<boolean>(false);

  /**
   * Keeps only the rules the selected chart declares support for.
   *
   * @param rules            Full rule list from the Guardrails response.
   * @param allowedRuleNames Rule names accepted by the selected chart option.
   */
  const filterRules = (rules: Rules[], allowedRuleNames: string[] = []) => {
    setApplicableRules(rules.filter((rule: any) => allowedRuleNames.includes(rule.ruleName)));
  };

  /**
   * Validates the current selection against every applicable rule.
   *
   * A rule matches when both the questions and the calculations counts fall inside its
   * `Min`/`Max` boundaries. When nothing matches, the hook works out *which* dimension is at
   * fault so the UI can highlight the right picker; if neither dimension can be blamed
   * individually, both are flagged.
   *
   * @returns The matching rules, or an empty array when the selection is invalid.
   */
  const selectRule = (sQuestions?: SelectOptions[], sCalculations?: SelectOptions[]): Rules[] => {
    if (sQuestions && sCalculations && applicableRules.length) {
      const inRange = (count: number, bounds: { min: number; max: number }) =>
        count >= bounds.min && count <= bounds.max;

      const sRule = applicableRules.filter(
        (rule: Rules) =>
          inRange(sQuestions.length, rule.questions) &&
          inRange(sCalculations.length, rule.calculations)
      );

      const qFailedRule = applicableRules.filter(
        (rule: Rules) =>
          !inRange(sQuestions.length, rule.questions) &&
          inRange(sCalculations.length, rule.calculations)
      );

      const cFailedRule = applicableRules.filter(
        (rule: Rules) =>
          inRange(sQuestions.length, rule.questions) &&
          !inRange(sCalculations.length, rule.calculations)
      );

      const isAnyRuleFailed = sRule.length === 0;
      const isQuestionRuleFailed = qFailedRule.length > 0;
      const isCalculationRuleFailed = cFailedRule.length > 0;

      setRequiredQuestionsCalculations(isAnyRuleFailed);
      if (isAnyRuleFailed && (isQuestionRuleFailed || isCalculationRuleFailed)) {
        setRequiredQuestions(isQuestionRuleFailed);
        setRequiredCalculations(isCalculationRuleFailed);
      } else if (isAnyRuleFailed && !(isQuestionRuleFailed || isCalculationRuleFailed)) {
        setRequiredQuestions(true);
        setRequiredCalculations(true);
      } else {
        setRequiredQuestions(false);
        setRequiredCalculations(false);
      }
      return sRule;
    }

    // No selection yet (or no rules loaded): treat the widget as incomplete.
    // WHY (known quirk, intentionally preserved): the original code called `setRequiredCalculations`
    // twice instead of also setting `setRequiredQuestions`. Changing it would alter which picker is
    // highlighted before the first interaction.
    // TODO(diy-rules-quirk): revisit once there is regression coverage for the empty selection case.
    setRequiredQuestionsCalculations(true);
    setRequiredCalculations(true);
    setRequiredCalculations(true);
    return [];
  };

  /** A picker is only rendered when some applicable rule accepts at least one item of that kind. */
  const showQuestionCalculation = (ruleType: RuleType): boolean =>
    applicableRules.filter((rule: any) => rule[ruleType].max > 0).length > 0;

  return {
    applicableRules,
    responseRules,
    setResponseRules,
    filterRules,
    requiredQuestionsCalculations,
    requiredQuestions,
    requiredCalculations,
    selectRule,
    showQuestionCalculation,
  };
};
